import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { dismissWinnerPopup as dismissWinnerPopupAction } from '~/actions/BestInDental';
import Button from '~/commonComponents/Button';
import { Column } from '~/commonComponents/Column';
import { Icon } from '~/commonComponents/Icon';
import { Pressable } from '~/commonComponents/Pressable';
import { Row } from '~/commonComponents/Row';
import { Text } from '~/commonComponents/Text';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useDismissWinnerPopup } from '~/domain/BestInDental';
import { showErrorMessage } from '~/domain/utils';
import { useToggle } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import BestInDentalBadge from '~/images/best-in-dental.png';
import { IOfficeBestInDentalWinnerDetail } from '~/models';
import { Colors } from '~/themes/colors';

/**
 * BestInDentalWinnerPopup Component
 *
 * Displays a one-time congratulatory popup for Best-in-Dental award winners.
 * The popup appears on first login after being awarded "Winner" status.
 *
 * Features:
 * - Backend-persisted dismissal state (cross-device consistency)
 * - Handles multiple winning years (shows most recent undismissed)
 * - Error handling with user feedback
 * - "Go to Site" button opens Best in Dental dashboard
 * - "Close" button dismisses popup
 */
export const BestInDentalWinnerPopup = () => {
  const dispatch = useDispatch();

  const currentUser = useUserSelector();
  const [isOpen, controls] = useToggle(false);
  const dismissWinnerPopup = useDismissWinnerPopup();

  const isWinner = currentUser?.office_info?.bestInDental?.status === 'winner';
  const winnerDetails = currentUser?.office_info?.bestInDental?.winnerDetails;

  const mostRecentUnDismissedWin: IOfficeBestInDentalWinnerDetail = useMemo(() => {
    if (!isWinner || !winnerDetails) {
      return null;
    }

    return [...winnerDetails].sort((a, b) => b.winningYear - a.winningYear)[0] || null;
  }, [isWinner, winnerDetails]);

  useEffect(() => {
    if (
      currentUser &&
      isWinner &&
      mostRecentUnDismissedWin &&
      !mostRecentUnDismissedWin.popupDismissedAt
    ) {
      controls.setOn();
      return;
    }

    controls.setOff();
  }, [controls, currentUser, isWinner, mostRecentUnDismissedWin]);

  const handleDismiss = async (year: number) => {
    try {
      await dismissWinnerPopup.mutateAsync({ year });
      controls.setOff();
    } catch (error: any) {
      showErrorMessage({
        error,
        defaultMessage: 'Failed to save preference. Please try again.',
      });
    }

    dispatch(dismissWinnerPopupAction(year));
  };

  const handleClose = () => {
    if (!mostRecentUnDismissedWin) return;
    handleDismiss(mostRecentUnDismissedWin.winningYear);
  };

  const handleGoToSite = () => {
    if (!mostRecentUnDismissedWin) return;

    window.open('https://bid.gotu.com/', '_blank');

    handleDismiss(mostRecentUnDismissedWin.winningYear);
  };

  if (!isOpen || !mostRecentUnDismissedWin) {
    return null;
  }

  return (
    // @ts-expect-error - ConfirmPopup still written in JavaScript
    <ConfirmPopup
      modalStyle={{ maxWidth: 500, maxHeight: 560, paddingTop: 57 }}
      Icon={() => (
        <img alt="Best In Dental Badge" src={BestInDentalBadge} width={164} height={184} />
      )}
      title="You've Won Best in Dental!"
      titleStyle={{ marginBlock: 16 }}
      description={
        <Column>
          <Text color={Colors.neutral_500}>
            You show up for your team and everyone who walks through your door. That commitment to
            the people around you is what makes your practice truly stand out.
            <br />
            <br />
            Visit your Best in Dental Award dashboard to view your benefits!
          </Text>
        </Column>
      }
      descriptionStyle={{ marginBottom: 24, width: '85%' }}
    >
      <Row gap={16}>
        <Button
          style={{ backgroundColor: Colors.secondary_500, width: 152 }}
          text="Close"
          onClick={handleClose}
          disabled={dismissWinnerPopup.isPending}
        />
        <Pressable onPress={handleGoToSite} disabled={dismissWinnerPopup.isPending}>
          <Row className="button medium primary" gap={6} width={152}>
            <Icon name="external-link" color={Colors.white} />
            <Text bold color={Colors.white} fontSize={18}>
              Go to Site
            </Text>
          </Row>
        </Pressable>
      </Row>
    </ConfirmPopup>
  );
};
